import { HttpErrorResponse } from '@angular/common/http';
import { Component, effect, inject, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { BaseDirective } from '@shared/directives/base/base.directive';
import { LoaderComponent, ToastComponent, ToastType } from 'cve-ui';
import { environment } from '../environments/environment';
import { BreakpointService } from './infrastructure/services/breakpoint.service';
import { GoogleAnalyticService } from './infrastructure/services/google-analytic.service';
import { ImpersonationService } from './infrastructure/services/impersonation.service';
import { ImpersonationBannerComponent } from './shared/components/impersonation-banner/impersonation-banner.component';

@Component({
    selector: 'app-root',
    imports: [
        RouterOutlet,
        LoaderComponent,
        ToastComponent,
        ImpersonationBannerComponent,
    ],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
})
export class AppComponent extends BaseDirective implements OnInit {
    breakpointService = inject(BreakpointService);
    googleAnalyticService = inject(GoogleAnalyticService);
    readonly impersonationService = inject(ImpersonationService);

    constructor() {
        super();
        this.breakpointService.set();
        this.loadGTM();

        effect(() => {
            if (this.impersonationService.newSessionDetected()) {
                this.router.navigate(['/impersonation-errors/expired']);
            }
        });
    }

    override ngOnInit(): void {
        super.ngOnInit();

        // Capture ?code= parameter from URL for Support Impersonation
        const code = this.impersonationService.getCodeFromUrl();
        if (code && !this.impersonationService.hasCodeBeenRedeemed(code)) {
            this.impersonationService.redeemCode(code).subscribe({
                next: () => {
                    this.impersonationService.cleanCodeFromUrl();
                    this.router.navigate(['/vehicles']);
                },
                error: (err: HttpErrorResponse) => {
                    console.error('[AppComponent] Error redeeming impersonation code:', err);
                    this.impersonationService.cleanCodeFromUrl();
                    const isRateLimited = err.status === 429;
                    if (isRateLimited) {
                        this.router.navigate(['/impersonation-errors/rate-limited']);
                    } else {
                        this.toastService.show({
                            type: ToastType.error,
                            title: this.translateService.instant('shared.toast.impersonation_redeem_error.title'),
                            label: this.translateService.instant('shared.toast.impersonation_redeem_error.label'),
                            from: 'top',
                        });
                        this.router.navigate(['/login']);
                    }
                },
            });
        }
    }

    reloadPage(): void {
        window.location.reload();
    }

    closeTab(): void {
        window.close();
    }

    private loadGTM(): void {
        if (!environment.google_tag_manager) {
            return; // GTM disabled in this environment
        }

        const gtmId = environment.google_tag_manager;

        const gtmAuthParams = environment.gtm_auth
            ? `&gtm_auth=${environment.gtm_auth}&gtm_preview=${environment.gtm_preview}&gtm_cookies_win=${environment.gtm_cookies_win}`
            : '';

        const gtmScript = document.createElement('script');
        gtmScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl+'${gtmAuthParams}';f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gtmId}');`;

        if (document.head.firstChild) {
            document.head.insertBefore(gtmScript, document.head.firstChild);
        } else {
            document.head.appendChild(gtmScript);
        }

        const noscript = document.createElement('noscript');
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}${gtmAuthParams}`;
        iframe.height = '0';
        iframe.width = '0';
        iframe.style.display = 'none';
        iframe.style.visibility = 'hidden';
        noscript.appendChild(iframe);

        if (document.body.firstChild) {
            document.body.insertBefore(noscript, document.body.firstChild);
        } else {
            document.body.appendChild(noscript);
        }
    }
}
